import { UploaderPlugin } from '../uploadcare/uploader-plugin.types';
import { PhoneUploadFile } from './phone-upload.models';

export type { UploaderPlugin } from '../uploadcare/uploader-plugin.types';

/** Add this to a <uc-config> source-list to show the source. */
export const PHONE_UPLOAD_SOURCE_ID = 'phone';
export const PHONE_UPLOAD_ACTIVITY_ID = 'gs-phone-upload';

const PLUGIN_ID = 'gs-phone-upload';
const ICON_NAME = 'gs-qr-code';
const LABEL_KEY = 'gs-phone-upload.source';

const QR_ICON =
  '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round">' +
  '<rect x="3.5" y="3.5" width="6" height="6" rx="1"/><rect x="14.5" y="3.5" width="6" height="6" rx="1"/>' +
  '<rect x="3.5" y="14.5" width="6" height="6" rx="1"/>' +
  '<path d="M6.5 6.5h0M17.5 6.5h0M6.5 17.5h0" stroke-width="2.4"/>' +
  '<path d="M14.5 14.5h2.5v2.5M20.5 14.5v0M14.5 20.5h0M17.5 20.5h3v-3"/></svg>';

export interface PhoneUploadPluginOptions {
  /**
   * Renders the QR panel into the uploader's activity area and returns a cleanup
   * function. Received files are passed to `onFiles`.
   */
  mountPanel(host: HTMLElement, onFiles: (files: PhoneUploadFile[]) => void): () => void;
}

/**
 * "Upload from Phone" source for the Uploadcare File Uploader. Picking it opens an
 * activity with a QR code; files sent from the phone are added to this uploader by
 * UUID, so they go through the same upload events as any other source.
 *
 *   config.plugins = [createPhoneUploadPlugin({ mountPanel })];
 *   <uc-config source-list="url,phone,...">
 */
export function createPhoneUploadPlugin(options: PhoneUploadPluginOptions): UploaderPlugin {
  return {
    id: PLUGIN_ID,
    setup({ pluginApi, uploaderApi }) {
      pluginApi.registry.registerIcon({ name: ICON_NAME, svg: QR_ICON });
      pluginApi.registry.registerL10n({ en: { [LABEL_KEY]: 'Scan to Upload' } });

      pluginApi.registry.registerSource({
        id: PHONE_UPLOAD_SOURCE_ID,
        label: LABEL_KEY,
        icon: ICON_NAME,
        onSelect() {
          uploaderApi.setCurrentActivity(PHONE_UPLOAD_ACTIVITY_ID);
          uploaderApi.setModalState(true);
        }
      });

      pluginApi.registry.registerActivity({
        id: PHONE_UPLOAD_ACTIVITY_ID,
        render(host) {
          return options.mountPanel(host, files => {
            files.forEach(file =>
              uploaderApi.addFileFromUuid(file.uuid, { fileName: file.original_filename || undefined })
            );
            uploaderApi.setCurrentActivity('upload-list');
          });
        }
      });
    }
  };
}
